'use client';

import { Box, Slider, Typography, IconButton, Stack, Paper, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ZoomInMapIcon from '@mui/icons-material/ZoomInMap';

interface ScreenSliderProps {
  zoom: number;
  setZoom: (zoom: number) => void;
  min?: number;
  max?: number;
  step?: number;
}

export default function ScreenSlider({
  zoom,
  setZoom,
  min = 50,
  max = 150,
  step = 5,
}: ScreenSliderProps) {
  const handleZoomChange = (_event: Event | React.SyntheticEvent, newValue: number | number[]) => {
    if (typeof newValue === 'number') {
      setZoom(newValue);
    }
  };

  const handleZoomIn = () => {
    setZoom(Math.min(max, zoom + step));
  };

  const handleZoomOut = () => {
    setZoom(Math.max(min, zoom - step));
  };

  return (
    <Paper
      elevation={2}
      sx={{
        pl: 1.5,
        pr: 2,
        py: 1,
        borderRadius: 3,
        display: 'inline-block',
        background: 'rgba(255,255,255,0.85)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        minWidth: 320,
        maxWidth: 400,
        mr: 2,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <ZoomInMapIcon fontSize="small" sx={{ color: 'primary.main', mr: 0.5 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mr: 1 }}>
          Zoom
        </Typography>
        <IconButton onClick={handleZoomOut} disabled={zoom <= min} size="small" color="primary" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '50%', background: zoom <= min ? 'action.disabledBackground' : 'background.paper', }}>
          <RemoveIcon fontSize="small" />
        </IconButton>
        <Slider
          value={zoom}
          onChange={handleZoomChange}
          min={min}
          max={max}
          step={step}
          sx={{
            mx: 1,
            width: 140,
            color: 'primary.main',
            height: 4,
            '& .MuiSlider-thumb': {
              width: 18,
              height: 18,
              backgroundColor: 'primary.main',
              border: '2px solid white',
              boxShadow: 1,
            },
            '& .MuiSlider-rail': {
              opacity: 0.3,
              backgroundColor: 'primary.main',
            },
            '& .MuiSlider-track': {
              backgroundColor: 'primary.main',
            },
          }}
        />
        <Chip
          label={`${zoom}%`}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: '0.95rem',
            color: 'primary.main',
            background: 'primary.lighter',
            px: 1.5,
            borderRadius: 2,
            ml: 0.5,
          }}
        />
        <IconButton onClick={handleZoomIn} disabled={zoom >= max} size="small" color="primary" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '50%', background: zoom >= max ? 'action.disabledBackground' : 'background.paper', }}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
}